import { useState, useRef } from 'react';
// @ts-expect-error - TS2305 - Module '"@apollo/react-hooks"' has no exported member 'ApolloError'.
import { type QueryLazyOptions, useLazyQuery, type ApolloError } from '@apollo/react-hooks';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { handleApolloError } from '@atlassian/jira-issue-create-commons/src/common/utils/handle-apollo-error/index.tsx';
import type {
	QueryVariables,
	CreateConfigQueryResponseInContext,
	FetchConfigData,
	CreateConfigQueryResponseInContextWithML,
} from '../../common/types/index.tsx';
import {
	transformQueryResults,
	tranformModalLoadDataInContext,
} from '../../common/utils/fetch-config-query/index.tsx';
import { useSmartFieldsMachineLearning } from '../../common/utils/use-smart-fields-ml/index.tsx';
import { getCreateConfigQuery } from '../common/gql.tsx';
import { useModalLoadUiTaskEvents } from '../../../issue-create-analytics/src/controllers/use-modal-load-ui-task-events/index.tsx';
import { getCreateConfigUserPreferredQueryInContext } from './gql.tsx';
import type { UseFetchCreateConfigQueryInContext } from './types.tsx';

const useFetchCreateConfigQueryInContext: UseFetchCreateConfigQueryInContext = (props = {}) => {
	const { fireModalLoadTaskFailEvent } = useModalLoadUiTaskEvents();
	const { displayOnlyRequiredFields = false, triggerPointKey, isCreateLinkedIssue = false } = props;
	const [loading, setLoading] = useState(false);
	const [fetchError, setFetchError] = useState<ApolloError | null>(null);
	const apiStartTime = useRef<number>(0);
	const [apiLatency, setApiLatency] = useState<number>(0);
	const [data, setData] = useState<FetchConfigData>({
		fieldTabs: [],
		fieldConfig: {},
		contextFields: {},
		meta: {
			areRequiredFieldsSupported: true,
			queryErrorMessage: '',
		},
		additionalServices: {},
	});

	const { withMachineLearning } = useSmartFieldsMachineLearning();

	const createConfigQuery = fg('new-issue-create-fetch-config-query')
		? getCreateConfigQuery({
				isInContext: true,
				withML: withMachineLearning,
				isCreateLinkedIssue,
				isCreateLinkedIssueFromIssueViewFGOn: !!fg('create_linked_issues_trigger_point_issue_view'),
				isJsmRespondersFieldEnabled: fg('jsm-responders-field-in-gicx-ui'),
			})
		: getCreateConfigUserPreferredQueryInContext(withMachineLearning, isCreateLinkedIssue);

	const [fetchConfigInContext] = useLazyQuery<
		CreateConfigQueryResponseInContext | CreateConfigQueryResponseInContextWithML,
		QueryVariables
	>(createConfigQuery, {
		fetchPolicy: 'no-cache',
		onCompleted: (queryData) => {
			setApiLatency(Math.floor(performance.now() - apiStartTime.current));
			setData(
				transformQueryResults(tranformModalLoadDataInContext(queryData), displayOnlyRequiredFields),
			);
			setLoading(false);
		},
		onError: (error: ApolloError) => {
			setFetchError(error);
			const errorType = handleApolloError(error);
			fireErrorAnalytics({
				meta: {
					id: 'useFetchCreateConfigQueryInContext',
					packageName: 'jiraIssueCreate',
					teamName: 'gryffindor',
				},
				error,
				attributes: {
					// TODO: clean up after rollout of FG issue-create-fire-error-analytics-refactor
					errorType,
					// TODO: look to get this into common hook and populate it everywhere.
					triggerPointKey,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			if (errorType === 'graphQLError' && fg('global_issue_create_modal_view_task_fail_event')) {
				fireModalLoadTaskFailEvent();
			}
		},
	});
	const handleFetch = useRef((queryVariables?: QueryLazyOptions<QueryVariables>) => {
		setLoading(true);
		apiStartTime.current = performance.now();
		fetchConfigInContext(queryVariables);
	});
	return [
		{
			isLoading: loading,
			fieldTabs: data.fieldTabs,
			fieldConfig: data.fieldConfig,
			contextFields: data.contextFields,
			meta: data.meta,
			error: fetchError,
			apiLatency,
			additionalServices: data.additionalServices,
		},
		handleFetch.current,
	];
};
export default useFetchCreateConfigQueryInContext;
